import { t } from "@lingui/macro";
import { atom } from "jotai";

import { reorderPrivateRounds } from "@/features/BookingRounds/utils/reorderPrivateRounds";
import {
  CreateRequestBookingInput,
  CreateMemberRoundInput,
  CreateGuestRoundInput,
  NewGuestRoundInput,
} from "@/features/BookingWizard/hooks/useCreateNewRequestBooking/__generated__/useCreateNewRequestBookingMutation.graphql";

import { BookingWizard } from "../../../types";
import { BookingWizardDataAtom } from "../../stores";
import { processPrivateRequestRounds } from "../../utils/processPrivateRequestRounds";

type CustomerRoundInput = NonNullable<
  CreateRequestBookingInput["customerRounds"]
>[number];

type PrivateRounds = Pick<
  CreateRequestBookingInput,
  "memberRounds" | "guestRounds" | "newGuestRounds"
>;

const buildCustomerRounds = (
  rounds: BookingWizard.RequestRound[] | undefined,
  courseLayoutId: string | undefined,
  groupSize: number | undefined,
  requestGroupCount: number
): CustomerRoundInput[] => {
  return Array.from({ length: requestGroupCount }, (_, bookingIndex) => {
    const groupRounds = (rounds ?? []).filter(
      (round) => (round.bookingIndex ?? 0) === bookingIndex
    );
    const base: CustomerRoundInput[] = groupRounds.map((round, index) => ({
      index,
      ...(requestGroupCount > 1 ? { bookingIndex } : {}),
      playerId: round.player?.id ?? null,
      courseLayoutId: courseLayoutId ?? null,
    }));

    if (groupSize && base.length < groupSize) {
      const remainingRounds = groupSize - base.length;
      base.push(
        ...Array.from({ length: remainingRounds }, (_, index) => ({
          index: base.length + index,
          ...(requestGroupCount > 1 ? { bookingIndex } : {}),
          playerId: null,
          courseLayoutId: courseLayoutId ?? null,
        }))
      );
    }

    return base;
  }).flat();
};

const buildPrivateRounds = (
  rounds: BookingWizard.RequestRound[] | undefined,
  courseLayoutId: string | undefined | null,
  ownerClientId: string,
  requestGroupCount: number
): PrivateRounds => {
  const groups = Array.from(
    { length: requestGroupCount },
    (_, bookingIndex) => {
      const groupRounds = (rounds ?? []).filter(
        (round) => (round.bookingIndex ?? 0) === bookingIndex
      );
      const memberRounds = processPrivateRequestRounds<CreateMemberRoundInput>(
        groupRounds,
        ["member", "memberHold"],
        courseLayoutId ?? null,
        undefined,
        requestGroupCount > 1
      );
      const guestRounds = processPrivateRequestRounds<CreateGuestRoundInput>(
        groupRounds,
        ["guest", "unknownGuest"],
        courseLayoutId ?? null,
        undefined,
        requestGroupCount > 1
      );
      const newGuestRounds = processPrivateRequestRounds<NewGuestRoundInput>(
        groupRounds,
        ["newGuest"],
        courseLayoutId ?? null,
        (round) => {
          const safeFirst = round.player?.firstName?.trim() ?? "";
          const safeLast = round.player?.lastName?.trim() ?? "";
          const fullName = [safeFirst, safeLast]
            .filter(Boolean)
            .join(" ")
            .trim();
          return { name: fullName.length > 0 ? fullName : t`New Guest` };
        },
        requestGroupCount > 1
      );
      const reordered = reorderPrivateRounds({
        memberRounds,
        guestRounds,
        newGuestRounds,
        ownerClientId,
      });

      const addBookingIndex = <T extends { bookingIndex?: number | null }>(
        rounds: T[]
      ): T[] =>
        requestGroupCount > 1
          ? rounds.map((round) => ({ ...round, bookingIndex }))
          : rounds;

      return {
        memberRounds: addBookingIndex(reordered.memberRounds ?? []),
        guestRounds: addBookingIndex(reordered.guestRounds ?? []),
        newGuestRounds: addBookingIndex(reordered.newGuestRounds ?? []),
      };
    }
  );

  let roundIndexOffset = 0;
  const groupsWithGlobalIndexes = groups.map((group) => {
    const groupRoundCount =
      (group.memberRounds?.length ?? 0) +
      (group.guestRounds?.length ?? 0) +
      (group.newGuestRounds?.length ?? 0);
    const offset = roundIndexOffset;
    roundIndexOffset += groupRoundCount;

    const reindex = <T extends { index?: number | null }>(rounds: T[]): T[] =>
      rounds.map((round) => ({
        ...round,
        index: (round.index ?? 0) + offset,
      }));

    return {
      memberRounds: reindex(group.memberRounds ?? []),
      guestRounds: reindex(group.guestRounds ?? []),
      newGuestRounds: reindex(group.newGuestRounds ?? []),
    };
  });

  return groupsWithGlobalIndexes.reduce<PrivateRounds>(
    (accumulator, group) => ({
      memberRounds: [
        ...(accumulator.memberRounds ?? []),
        ...(group.memberRounds ?? []),
      ],
      guestRounds: [
        ...(accumulator.guestRounds ?? []),
        ...(group.guestRounds ?? []),
      ],
      newGuestRounds: [
        ...(accumulator.newGuestRounds ?? []),
        ...(group.newGuestRounds ?? []),
      ],
    }),
    { memberRounds: [], guestRounds: [], newGuestRounds: [] }
  );
};

export const createRequestBookingInputAtom =
  atom<CreateRequestBookingInput | null>((get) => {
    const bookingWizardData = get(BookingWizardDataAtom);

    if (!bookingWizardData || bookingWizardData.type !== "RequestBooking") {
      return null;
    }

    const {
      startSlotId,
      requestedTime,
      minTime,
      maxTime,
      courseLayoutId,
      rounds,
      groupSize,
      isPrivate,
      ownerClientId,
      requestCoursePreferences,
      requestGroupCount,
    } = bookingWizardData;

    if (!startSlotId || !requestedTime) {
      return null;
    }

    const input: CreateRequestBookingInput = {
      startSlotId,
      request: {
        time: requestedTime,
        minTime: minTime || requestedTime,
        maxTime: maxTime || requestedTime,
        ...(requestCoursePreferences && requestCoursePreferences.length > 0
          ? { requestCoursePreferences }
          : {}),
      },
      isDraft: true,
      notifyMember: true,
      ...(isPrivate
        ? buildPrivateRounds(
            rounds,
            courseLayoutId ? courseLayoutId : null,
            ownerClientId,
            requestGroupCount ?? 1
          )
        : {
            customerRounds: buildCustomerRounds(
              rounds,
              courseLayoutId,
              groupSize,
              requestGroupCount ?? 1
            ),
          }),
    };

    return input;
  });
